import { GetPartnerBusinessProfileResponse } from '@/api/type/types';
import { Images } from '@/assets';
import { MultiLineText } from '@/components/text/MultiLineText';
import { useLoading } from '@/context/LoadingContext';
import { useAlert } from '@/hooks/useAlert';
import {
  FileTransferService,
  FileTransferSource,
  FileUploadSource,
} from '@/libs/r2-service-client';
import { useAppSelector } from '@/stores/hooks';
import { businessProfile } from '@/stores/slices/business-profile/businessProfileSlice';
import { fonts } from '@/theme/fonts';
import { formatAttachedFile } from '@/utils/file/formatAttachedFile';
import { getFileUrl } from '@/utils/file/getFileUrl';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

interface ProfileImgUploadProps {
  isEdit: boolean;
  profile: GetPartnerBusinessProfileResponse;
}
const ProfileImgUpload = ({ isEdit, profile }: ProfileImgUploadProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { startLoading, stopLoading } = useLoading();
  const dispatch = useDispatch();
  const { putBusinessProfile } = useAppSelector(
    (state) => state.businessProfile
  );
  const { showAlert } = useAlert();
  const { t } = useTranslation();

  const onClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadFiles = useCallback(
    async (files: File[]) => {
      if (!files.length) return;
      startLoading();
      const MAX_TOTAL_SIZE = 20 * 1024 * 1024; // 20MB
      const allowExtensions = ['jpeg', 'jpg', 'png', 'bmp'];
      try {
        // 확장자 체크
        const invalidFiles = files.filter((file) => {
          const ext = file.name.split('.').pop()?.toLowerCase();
          return !ext || !allowExtensions.includes(ext);
        });
        if (invalidFiles.length) {
          showAlert({
            message: t('onlyFilesAreSupported', {
              type: 'JPEG/JPG, PNG, Bmp',
            }),
          });
          return;
        }
        // 용량 체크
        const totalSize = files.reduce((acc, file) => acc + file.size, 0);
        if (totalSize > MAX_TOTAL_SIZE) {
          showAlert({
            message: t('fileUploadLimitfileSizeMustBeMbOrLess', {
              size: '20MB',
            }),
          });
          return;
        }
        const uploadSource = new FileUploadSource('USER_UPLOAD');
        files.forEach((file) => uploadSource.addBrowserData(file));

        const transferSource = new FileTransferSource(uploadSource);
        const fileService = new FileTransferService();

        const uploadResult = await fileService.transfer(transferSource);
        if (!uploadResult.upload) return;
        const attachedFiles = formatAttachedFile(uploadResult.upload);
        dispatch(
          businessProfile.putBusinessProfile({ profileImage: attachedFiles[0] })
        );

        if (fileInputRef.current) fileInputRef.current.value = '';
      } finally {
        stopLoading();
      }
    },
    [dispatch, showAlert, startLoading, stopLoading, t]
  );

  const onFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0)
        if (e.target.files) {
          uploadFiles(Array.from(e.target.files));
        }
    },
    [uploadFiles]
  );

  return (
    <Container>
      <Title>{t('profileSettings')}</Title>
      <Section>
        <ImgWrap>
          <Profile
            src={getFileUrl(
              putBusinessProfile
                ? putBusinessProfile.profileImage.id
                : profile.profileImage.id
            )}
            alt="profile-img"
          />
          {isEdit && (
            <EditButton onClick={onClickUpload}>{t('modify2')}</EditButton>
          )}
          <FileInput type="file" ref={fileInputRef} onChange={onFileChange} />
        </ImgWrap>
        <Desc>
          {t('imagesWillBeAppliedIn100x100Size')}
          <br />
          <MultiLineText
            translationKey={t('fileFormatsJpegJpgPngBmpMaxSize20mb')}
          />
        </Desc>
      </Section>
    </Container>
  );
};

export default ProfileImgUpload;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: start;
  text-align: start;
  gap: 14px;
  padding-bottom: 6px;
  border-bottom: 1px dotted #6a96f8;
`;

const Title = styled.div`
  font-weight: ${fonts.weight.bold};
  font-size: 20px;
  line-height: 140%;
  color: #000000;
`;

const Section = styled.div`
  height: 159px;
  display: flex;
  align-items: start;
  padding-left: 40px;
  gap: 69px;
`;

const ImgWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const Profile = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 999px;
  border: 2px solid #e4e7fb;
`;

const EditButton = styled.button`
  border-radius: 9px;
  border: 1px solid #9a9a9a;
  height: 30px;
  width: 69px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  line-height: 140%;
  color: #9a9a9a;
`;

const Desc = styled.div`
  font-size: 15px;
  line-height: normal;
  color: #6896f8;
  height: 100px;
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const FileInput = styled.input`
  display: none;
`;
